import type { BeaconDisplayOptions, BeaconFunction, BeaconMethod } from '#shared/helpscout-beacon.config.dto';

const pendingOpen = ref(false);

// HelpScout's layer on the z-index ladder (docs/styling-tokens.md): every z-40 surface covers it.
const DISPLAY_CONFIG: BeaconDisplayOptions = { display: { zIndex: 30 } };

const isBeaconOpen = ref(false);

const handleOpen = () => {
  isBeaconOpen.value = true;
};

const handleClose = () => {
  isBeaconOpen.value = false;
};

const handleReady = () => {
  console.log('[Beacon] handleReady fired, pendingOpen:', pendingOpen.value);
  if (pendingOpen.value) {
    pendingOpen.value = false;
    setTimeout(() => {
      globalThis.Beacon?.('navigate', '/ask/');
      globalThis.Beacon?.('open');
    }, 0);
  }
};

const openBeacon = () => {
  console.log('[Beacon] openBeacon, info:', globalThis.Beacon?.('info'), 'pendingOpen:', pendingOpen.value);
  if (globalThis.Beacon?.('info')) {
    globalThis.Beacon('navigate', '/ask/');
    globalThis.Beacon('open');
    return;
  }
  pendingOpen.value = true;
};

export const useHelpscoutBeacon = () => {
  const { $appConfig } = useNuxtApp();
  const config = $appConfig.app?.composables?.helpscoutBeacon;
  const scriptSource = $appConfig.beacon?.scriptSrc;

  const beaconId = useLocalized(config?.ids);

  // crossorigin: false — HelpScout's CDN sends no CORS headers
  const { load } = useScript({ src: scriptSource ?? '#', crossorigin: false }, { trigger: 'manual' });

  const initBeacon = async () => {
    if (!scriptSource || !beaconId.value || globalThis.Beacon?.('info')) {
      return;
    }

    // HelpScout's embed stub: queues calls until the real API loads; `undefined` to 'info' means not mounted yet.
    const stub: BeaconFunction = (method: BeaconMethod, options?: unknown, data?: unknown): undefined => {
      stub.readyQueue ??= [];
      stub.readyQueue.push({ method, options, data });
    };
    globalThis.Beacon = stub;

    globalThis.Beacon('init', beaconId.value);

    globalThis.Beacon('config', DISPLAY_CONFIG);

    // once ready — queued before load() so HelpScout processes it on startup alongside init
    globalThis.Beacon('once', 'ready', handleReady);

    // on open / close — queued the same way
    globalThis.Beacon('on', 'open', handleOpen);
    globalThis.Beacon('on', 'close', handleClose);

    await load();
  };

  // Locale change — script already loaded, 'ready' does not re-fire after destroy+init
  const switchBeacon = () => {
    if (!beaconId.value || !globalThis.Beacon?.('info')) {
      return;
    }
    globalThis.Beacon('destroy');
    globalThis.Beacon('init', beaconId.value);
    globalThis.Beacon('config', DISPLAY_CONFIG);
    globalThis.Beacon('on', 'open', handleOpen);
    globalThis.Beacon('on', 'close', handleClose);
  };

  return {
    initBeacon,
    switchBeacon,
    openBeacon,
    isBeaconOpen: readonly(isBeaconOpen),
  };
};
